/**
 * Web mirror of the C2 process-normalization seam
 * (`apps/api/src/modules/parts-library/process-normalization.ts`, UID-162).
 *
 * `apps/web` and `apps/api` are separate npm workspaces (see
 * `monorepo-layout` conventions) with no shared runtime import path between
 * them for a plain policy module like this one, so this is a DELIBERATE,
 * literal mirror rather than an import — the C3 task explicitly calls for
 * "add/mirror a tiny web copy... use the SAME mapping" when the source is
 * server-only. The RULES table below and the exported `normalizeProcess`
 * behavior are byte-for-byte the same decision logic as the server copy;
 * any future rule addition must land in BOTH files (or, better, promote
 * this into `packages/shared` once a second FE consumer needs it — flagged
 * as a follow-up, not attempted here to keep this story's diff scoped to
 * C3).
 *
 * Used by `EditConfigurationModal` to pre-select the Process `<select>` from
 * a part's stored `config_attributes.process`, which may be a legacy
 * free-form string (e.g. `"CNC Milling"`) rather than one of the five
 * `PartProcess` enum tokens the C0 contract defines.
 */

export const PART_PROCESSES = [
  'cnc_machining',
  'sheet_metal_fabrication',
  'injection_molding',
  'urethane_casting',
  '3d_printing',
  'compression_molding',
  'die_casting',
  'tube_bending',
] as const

export type PartProcess = (typeof PART_PROCESSES)[number]

export function isPartProcess(value: unknown): value is PartProcess {
  return typeof value === 'string' && (PART_PROCESSES as readonly string[]).includes(value)
}

/** Same defined, non-crashing fallback as the server copy — see that file's
 * doc comment for the rationale (most common process, safest default
 * preselect; the user can always re-pick). */
export const PROCESS_NORMALIZATION_FALLBACK: PartProcess = 'cnc_machining'

const RULES: ReadonlyArray<{ readonly all: readonly string[]; readonly process: PartProcess }> = [
  { all: ['injection'], process: 'injection_molding' },
  { all: ['sheet', 'metal'], process: 'sheet_metal_fabrication' },
  { all: ['urethane'], process: 'urethane_casting' },
  { all: ['3d'], process: '3d_printing' },
  { all: ['additive'], process: '3d_printing' },
  { all: ['sla'], process: '3d_printing' },
  { all: ['sls'], process: '3d_printing' },
  { all: ['fdm'], process: '3d_printing' },
  { all: ['cnc'], process: 'cnc_machining' },
  { all: ['mill'], process: 'cnc_machining' },
  { all: ['machining'], process: 'cnc_machining' },
  { all: ['turning'], process: 'cnc_machining' },
  // UID-93: the 2 real Fictiv processes added when PartProcess was extended
  // from 5 to all 7 (compression molding, die casting).
  { all: ['compression'], process: 'compression_molding' },
  { all: ['die', 'casting'], process: 'die_casting' },
  // Motorcycle-subframe demo BOM: a genuine 8th process outside the
  // ErpProcessMaterialData taxonomy.
  { all: ['tube', 'bending'], process: 'tube_bending' },
]

function canonicalize(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Map a legacy/DFM process string onto a {@link PartProcess} enum token.
 * SAME mapping as `apps/api`'s `normalizeProcess` — see that module's doc
 * comment for the full behavior contract (exact enum passthrough → known
 * legacy phrase → defined fallback; never throws).
 */
export function normalizeProcess(raw: string | null | undefined): PartProcess {
  if (typeof raw !== 'string') {
    return PROCESS_NORMALIZATION_FALLBACK
  }
  if (isPartProcess(raw)) {
    return raw
  }
  const canonical = canonicalize(raw)
  if (canonical === '') {
    return PROCESS_NORMALIZATION_FALLBACK
  }
  const collapsed = canonical.replace(/ /g, '_')
  if (isPartProcess(collapsed)) {
    return collapsed
  }
  const tokens = new Set(canonical.split(' '))
  for (const rule of RULES) {
    if (rule.all.every((t) => tokens.has(t))) {
      return rule.process
    }
  }
  return PROCESS_NORMALIZATION_FALLBACK
}
